import { useEffect, useState } from "react";
import { Plus, Trash2, Circle, Loader2, X, KeyRound, Save, Monitor, Smartphone, Tablet, LogIn, LogOut, Clock, ChevronRight, Copy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const input = "rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 w-full";
const deviceOf = (ua = "") => { const u = ua.toLowerCase(); if (/iphone|android.*mobile|ipod/.test(u)) return "mobile"; if (/ipad|tablet|android/.test(u)) return "tablet"; return "desktop"; };
const DEV_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
const fmtDur = (s) => { if (s === null || s === undefined || s < 0) return "—"; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); if (h > 0) return `${h}h ${m}min`; if (m > 0) return `${m}min`; return "< 1min"; };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function MemberModal({ member, onClose, onChanged }) {
  const [form, setForm] = useState({ name: member.name || "", email: member.email || "", role: member.role || "agent" });
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [genPw, setGenPw] = useState("");

  useEffect(() => { api.get(`/staff/${member.id}/sessions`).then((r) => setSessions(r.data)).catch(() => {}); }, [member.id]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/staff/${member.id}`, form); toast.success("Fiche mise à jour"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const resetPassword = async (generate) => {
    if (!generate && newPw.length < 6) { toast.error("6 caractères minimum"); return; }
    try {
      const { data } = await api.post(`/staff/${member.id}/reset-password`, generate ? {} : { password: newPw });
      if (data.generated) { setGenPw(data.password); toast.success("Mot de passe généré"); }
      else { setNewPw(""); setGenPw(""); toast.success("Mot de passe modifié"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const online = sessions.filter((s) => s.online).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose} data-testid="member-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-brand text-cream flex items-center justify-center font-display text-lg">{(member.name || "?")[0]?.toUpperCase()}</div>
            <div><h3 className="font-semibold text-forest">{member.name}</h3><p className="text-xs text-stone capitalize">{member.role} {online > 0 && <span className="text-green-600">· en ligne</span>}</p></div>
          </div>
          <button onClick={onClose} data-testid="member-modal-close" className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Edit info */}
          <div>
            <p className="text-xs font-semibold text-stone uppercase tracking-wide mb-3">Informations</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-stone">Nom</label><input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" /></div>
              <div><label className="text-xs text-stone">Email</label><input className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" /></div>
              <div><label className="text-xs text-stone">Rôle</label><select className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="member-role"><option value="agent">Agent (service client)</option><option value="admin">Administrateur</option></select></div>
            </div>
            <button onClick={save} disabled={saving} data-testid="member-save" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand text-cream px-4 py-2 text-sm font-semibold hover:bg-forest disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Enregistrer</button>
          </div>

          {/* Reset password */}
          <div className="border-t border-border pt-5">
            <p className="text-xs font-semibold text-stone uppercase tracking-wide mb-3 flex items-center gap-1.5"><KeyRound size={13} /> Réinitialiser le mot de passe</p>
            <div className="flex flex-wrap gap-2 items-center">
              <input type="text" className={input + " max-w-xs"} placeholder="Nouveau mot de passe" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="member-newpw" />
              <button onClick={() => resetPassword(false)} data-testid="member-setpw" className="rounded-lg border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand/5">Définir</button>
              <button onClick={() => resetPassword(true)} data-testid="member-genpw" className="rounded-lg bg-sage text-forest px-4 py-2 text-sm font-semibold hover:bg-brand/10">Générer</button>
            </div>
            {genPw && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm" data-testid="member-genpw-result">
                <span className="text-amber-800">Mot de passe temporaire : <strong className="font-mono">{genPw}</strong></span>
                <button onClick={() => { navigator.clipboard.writeText(genPw); toast.success("Copié"); }} className="ml-auto p-1.5 rounded hover:bg-amber-100"><Copy size={14} /></button>
              </div>
            )}
          </div>

          {/* Sessions history */}
          <div className="border-t border-border pt-5">
            <p className="text-xs font-semibold text-stone uppercase tracking-wide mb-3 flex items-center gap-1.5"><Clock size={13} /> Historique de connexion ({sessions.length})</p>
            {sessions.length === 0 ? <p className="text-sm text-stone">Aucune connexion enregistrée.</p> : (
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {sessions.map((s) => {
                  const Icon = DEV_ICON[deviceOf(s.user_agent)] || Monitor;
                  return (
                    <div key={s.id} className="flex items-center gap-3 text-sm rounded-lg bg-[#F7F7F8] px-3 py-2">
                      <Circle size={8} className={s.online ? "fill-green-400 text-green-400" : "fill-stone/40 text-stone/40"} />
                      <Icon size={14} className="text-stone shrink-0" />
                      <span className="text-stone flex items-center gap-1"><LogIn size={12} className="text-green-600" /> {fmt(s.login_at)}</span>
                      <span className="text-stone flex items-center gap-1">{s.online ? <span className="text-green-600">en ligne</span> : <><LogOut size={12} /> {fmt(s.logout_at || s.last_seen)}</>}</span>
                      <span className="ml-auto text-forest flex items-center gap-1"><Clock size={12} className="text-brand" /> {fmtDur(s.duration_seconds)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Tous les champs sont requis"); return; }
    setSaving(true);
    try { await api.post("/staff", form); toast.success("Membre ajouté"); setForm({ name: "", email: "", password: "", role: "agent" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const remove = async (e, id) => { e.stopPropagation(); if (!window.confirm("Supprimer ce membre ?")) return; await api.delete(`/staff/${id}`); load(); };

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-1">Équipe</h1>
      <p className="text-stone mb-6">Cliquez sur un membre pour modifier sa fiche, son mot de passe et voir son historique de connexion</p>

      <div className="rounded-xl bg-white border border-border p-5 mb-6 grid sm:grid-cols-5 gap-3" data-testid="staff-form">
        <input className={input} placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
        <input className={input} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
        <input className={input} type="password" placeholder="Mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
        <select className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="agent">Agent</option><option value="admin">Admin</option></select>
        <button onClick={add} disabled={saving} data-testid="staff-add" className="rounded-lg bg-brand text-cream text-sm font-semibold flex items-center justify-center gap-1 hover:bg-forest disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Ajouter</button>
      </div>

      <div className="rounded-xl bg-white border border-border divide-y divide-border">
        {staff.map((s) => (
          <button key={s.id} onClick={() => setSelected(s)} className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[#F7F7F8] transition-colors" data-testid={`staff-${s.id}`}>
            <div className="flex items-center gap-3">
              <Circle size={10} className={s.is_online ? "fill-green-400 text-green-400" : "fill-stone text-stone"} />
              <div><p className="text-sm font-medium text-forest">{s.name}</p><p className="text-xs text-stone">{s.email} · <span className="capitalize">{s.role}</span></p></div>
            </div>
            <div className="flex items-center gap-1">
              {s.role !== "admin" && <span onClick={(e) => remove(e, s.id)} className="text-red-500 hover:bg-red-50 rounded-lg p-2" data-testid={`staff-delete-${s.id}`}><Trash2 size={16} /></span>}
              <ChevronRight size={16} className="text-stone" />
            </div>
          </button>
        ))}
      </div>

      {selected && <MemberModal member={selected} onClose={() => setSelected(null)} onChanged={() => { load(); setSelected(null); }} />}
    </div>
  );
}
